"use client";

import styles from "./styles.module.css";
import Button from "@components/atoms/Button";
import Dropdown from "@components/atoms/Dropdown";
import useRequest from "common/hooks/useRequest";
import { useFormik } from "formik";
import { useTranslations } from "next-intl";
import { useSnackbar } from "notistack";
import { countryList } from "public/files/countries";
import { useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import * as yup from "yup";

export default function ContactForm() {
  const t = useTranslations("contact");
  const { enqueueSnackbar } = useSnackbar();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [timeStamp, setTimestamp] = useState(new Date().getTime());
  const [first, setFirst] = useState(true);
  const [clearField, setClearField] = useState(false);

  const basicSchema = yup.object().shape({
    email: yup
      .string()
      .email(t("contactFormFields.errors.validEmail"))
      .required(t("contactFormFields.errors.requiredEmail")),
    name: yup
      .string()
      .matches(/^([^0-9]*)$/, t("contactFormFields.errors.validName"))
      .min(3, t("contactFormFields.errors.shortName"))
      .required(t("contactFormFields.errors.requiredName")),
    company: yup.string(),
    country: yup.string(),
    message: yup.string(),
  });

  const { request: sendInfo } = useRequest({
    hideErrorMessage: true,
    url: `${process.env.NEXT_PUBLIC_CONTACT_URL}`,
    method: "post",
    onSuccess: (response: any) => {
      setValues({
        name: "",
        email: "",
        country: "",
        company: "",
        message: "",
      });
      setFieldValue("country", "");
      setClearField(!clearField);
      setErrors({});
      setTouched({}, false);
      enqueueSnackbar(t("yourDataHasBeenSubmitted"), {
        variant: "success",
        autoHideDuration: 3000,
        anchorOrigin: {
          horizontal: "right",
          vertical: "top",
        },
      });
    },
    onError: (_err: any) => {
      enqueueSnackbar(t("somethingWentWrong"), {
        variant: "error",
        autoHideDuration: 3000,
        anchorOrigin: {
          horizontal: "right",
          vertical: "top",
        },
      });
    },
  });

  const { request: verifyUser } = useRequest({
    hideErrorMessage: true,
    url: "/verify-user/api",
    method: "post",
    onSuccess: (response: any) => {
      if (response.score > 0.7 && Object.keys(errors).length === 0) {
        sendInfo({ body: values });
      } else {
        enqueueSnackbar(t("doesNotSendData"), {
          variant: "error",
          autoHideDuration: 3000,
          anchorOrigin: {
            horizontal: "right",
            vertical: "top",
          },
        });
      }
    },
    onError: (_err: any) => {
      enqueueSnackbar(t("somethingWentWrong"), {
        variant: "error",
        autoHideDuration: 3000,
        anchorOrigin: {
          horizontal: "right",
          vertical: "top",
        },
      });
    },
  });

  const onSubmit = async (values: any) => {
    let token: string = "";
    const currentTime = new Date().getTime();
    const limit = 1000 * 60;
    if (currentTime - timeStamp > limit || first) {
      if (executeRecaptcha) {
        token = await executeRecaptcha("contact");
        verifyUser({ body: { token } });
      } else console.log("Execute recaptcha not yet available");
      setTimestamp(currentTime);
      setFirst(false);
    }
  };

  const {
    values,
    errors,
    touched,
    setValues,
    setErrors,
    setTouched,
    handleBlur,
    handleChange,
    handleSubmit,
    setFieldValue,
  } = useFormik({
    initialValues: {
      name: "",
      email: "",
      company: "",
      country: "",
      message: "",
    },
    validationSchema: basicSchema,
    onSubmit,
  });

  const handleSend = async () => {
    onSubmit(values);
  };

  return (
    <div className="mt-8 flex flex-col items-center w-full">
      <div className={styles["form-light"]}></div>
      <div className={styles.card}>
        <form onSubmit={handleSubmit} action={"LOGIN"}>
          <div className={styles.fields}>
            <div style={{ position: "relative" }}>
              <span>{t("contactFormFields.name")}</span>
              <div className={styles["input-border"]}>
                <input
                  value={values.name}
                  onChange={handleChange}
                  id="name"
                  type="text"
                  placeholder="Your name"
                  onBlur={handleBlur}
                />
              </div>
              {errors.name && touched.name && (
                <span className={styles["error-message"]}>* {errors.name}</span>
              )}
            </div>
            <div style={{ position: "relative" }}>
              <span>{t("contactFormFields.email")}</span>
              <div className={styles["input-border"]}>
                <input
                  value={values.email}
                  onChange={handleChange}
                  id="email"
                  type="text"
                  placeholder="your@email.com"
                  onBlur={handleBlur}
                />
              </div>
              {errors.email && touched.email && (
                <span className={styles["error-message"]}>
                  * {errors.email}
                </span>
              )}
            </div>
            <div style={{ position: "relative" }}>
              <span>{t("contactFormFields.company")}</span>
              <div className={styles["input-border"]}>
                <input
                  value={values.company}
                  onChange={handleChange}
                  id="company"
                  type="text"
                  placeholder="Company name"
                  onBlur={handleBlur}
                />
              </div>
            </div>
            <div style={{ paddingBottom: "0.5rem" }}>
              <span>{t("contactFormFields.country")}</span>
              <Dropdown
                options={countryList.countries}
                chosenOption={values.country}
                setChosenOption={setFieldValue}
                clearField={clearField}
                field={"country"}
              />
            </div>
          </div>
          <span>{t("contactFormFields.message")}</span>
          <div
            className={`${styles["input-border"]} mt-6`}
            style={{ height: "204px" }}
          >
            <textarea
              value={values.message}
              onChange={handleChange}
              id="message"
              placeholder="Type your message..."
              onBlur={handleBlur}
              className="text-black"
            />
          </div>
          <div className="text-[13px] text-start mt-[10px]">
            This site is protected by reCAPTCHA and the Google
            <a
              className="text-[#BEBEBE]"
              href="https://policies.google.com/privacy"
            >
              {" "}
              Privacy Policy
            </a>{" "}
            and
            <a
              className="text-[#BEBEBE]"
              // style={{ color: 'RGB(185 185 185)' }}
              href="https://policies.google.com/terms"
            >
              {" "}
              Terms of Service
            </a>{" "}
            apply.
          </div>
          <div
            className={styles["submit-button-container"]}
            onClick={(e) => {
              e.preventDefault();
              handleSend();
            }}
          >
            <Button
              translation={"contact.contactFormFields"}
              text={"button"}
              style={"w-[220px]"}
            />
          </div>
        </form>
      </div>
    </div>
  );
}
